/**
 * T-1g-READ — reconcile a server view with in-flight local changes.
 *
 * WHY THIS EXISTS. The server view is authoritative but LATE. A super resolves
 * a decision on a job site with no signal: the resolve lands durably in the
 * outbox and sits QUEUED. Without reconciliation the Decisions tab re-renders
 * from the server/cached view and the decision is open again — the exact
 * "my change didn't take" failure this codebase has now hit four separate ways
 * (log refresh, dead chips, dead-tap nav, and this). Showing a super their own
 * completed work undone is worse than showing nothing.
 *
 * WHAT COUNTS AS IN-FLIGHT. A change is applied over the server view when it
 * is either (a) not yet delivered at all, or (b) delivered AFTER the server
 * produced this view. A change delivered BEFORE the view is already accounted
 * for server-side — and if the server disagrees, the server wins, because it
 * has seen the fold and the device has not.
 *
 * WHAT THIS IS NOT. It is not a second source of truth and not a local fold: it
 * applies only the handful of fields a local emission actually asserts, and
 * leaves everything else exactly as the server returned it.
 */

import type { Decision } from "../slice/decision-slice";
import type { MasterItem } from "../components/MasterActionListView";
import type { MediaPartRef, OutboxRecord } from "../outbox/types";
import type { StatusValue } from "../components/MasterActionListView";

/** Records whose local change the server view cannot yet reflect. */
export function pendingRecords(records: OutboxRecord[], viewFetchedAt: number | null): OutboxRecord[] {
  return records.filter((r) => {
    // A REJECTED change was refused server-side — never apply it over the
    // server's own view, or the tab would show a change that did not happen.
    // A DRAFT was undone by the human and is not an assertion about anything.
    if (r.state === "rejected" || r.state === "draft") return false;
    if (r.state !== "delivered") return true;
    if (viewFetchedAt === null) return true;
    const delivered = r.deliveredAt ? Date.parse(r.deliveredAt) : NaN;
    // An unparseable/absent delivery time is treated as in-flight: over-applying
    // a change the super really did make is far safer than dropping it.
    return Number.isNaN(delivered) || delivered >= viewFetchedAt;
  });
}

type DecisionRoute =
  | { skill: "decision_log_dayone"; op: "resolve"; ref: string; rationale: string }
  | { skill: "decision_log_dayone"; op: "assign_owner"; ref: string; owner: string };

/** The decision a route targets.
 *
 * Reads `decision_ref` FIRST and `decision_id` only as a fallback. #13 changed the
 * emitted wire field from `decision_id` to `decision_ref` (Skill #3 locates a
 * canonical by CAPTURE id, so the register key was always rejected) — and this
 * reconciler kept reading the old name, which silently killed the in-flight
 * overlay: a resolve stopped disappearing from the walk-of-shame until the server
 * read caught up. The fallback is not dead weight; records emitted by an older
 * bundle can still be sitting in a phone's outbox right now. */
function targetRef(route: Record<string, unknown>): string | null {
  if (typeof route.decision_ref === "string" && route.decision_ref) return route.decision_ref;
  if (typeof route.decision_id === "string" && route.decision_id) return route.decision_id;
  return null;
}

function decisionRoute(rec: OutboxRecord): DecisionRoute | null {
  const route = rec.envelope.payload["route"] as Record<string, unknown> | undefined;
  if (!route || route.skill !== "decision_log_dayone") return null;
  const ref = targetRef(route);
  if (ref === null) return null;
  if (route.op === "resolve") {
    return {
      skill: "decision_log_dayone",
      op: "resolve",
      ref,
      rationale: typeof route.rationale === "string" ? route.rationale : "",
    };
  }
  if (route.op === "assign_owner") {
    return {
      skill: "decision_log_dayone",
      op: "assign_owner",
      ref,
      owner: typeof route.owner === "string" ? route.owner : "",
    };
  }
  return null;
}

/** Where a decision change made on this device is, as the outbox knows it.
 * `pending` = saved on the phone, not yet delivered (held/queued/sending);
 * `rejected` = the server refused it; `delivered` = the server accepted it.
 * The Decisions tab labels anything short of `delivered` as not yet synced,
 * so a queued change is never shown as the canonical state. */
export type DecisionMutationState = "pending" | "rejected" | "delivered";

export interface DecisionMutation {
  state: DecisionMutationState;
  createdAt: string;
  deliveredAt?: string;
  /** assign_owner only. */
  owner?: string;
  /** resolve only. */
  rationale?: string;
}

export interface DecisionMutations {
  assign?: DecisionMutation;
  resolve?: DecisionMutation;
}

/** The LATEST assign and resolve change per decision ref (the capture id the
 * routes carry), from the device outbox. Drafts are skipped — an undone change
 * asserts nothing. */
export function latestDecisionMutations(records: OutboxRecord[]): Record<string, DecisionMutations> {
  const out: Record<string, DecisionMutations> = {};
  for (const rec of records) {
    if (rec.state === "draft") continue;
    const route = decisionRoute(rec);
    if (!route) continue;
    const mutation: DecisionMutation = {
      state: rec.state === "delivered" ? "delivered" : rec.state === "rejected" ? "rejected" : "pending",
      createdAt: rec.createdAt,
      ...(rec.deliveredAt ? { deliveredAt: rec.deliveredAt } : {}),
      ...(route.op === "assign_owner" ? { owner: route.owner } : { rationale: route.rationale }),
    };
    const key = route.op === "assign_owner" ? "assign" : "resolve";
    const entry = (out[route.ref] ??= {});
    const prev = entry[key];
    if (!prev || Date.parse(prev.createdAt) <= Date.parse(rec.createdAt)) entry[key] = mutation;
  }
  return out;
}

/**
 * Apply in-flight decision changes over a server view.
 *
 * A pending RESOLVE marks the decision resolved locally. When the caller is
 * showing the open-only list it then drops out — which is the whole point: the
 * super resolved it, so it must stop appearing on the walk-of-shame even before
 * the signal comes back.
 */
export function reconcileDecisions(
  serverItems: Decision[],
  records: OutboxRecord[],
  viewFetchedAt: number | null,
): Decision[] {
  const pending = pendingRecords(records, viewFetchedAt);
  if (pending.length === 0) return serverItems;

  // The emissions carry `decision_id` (the stable register id), while a view
  // item is keyed by both `decision_id` and its capture `id` — match on either,
  // since the resolve picker and the detail screen source them differently.
  const resolved = new Map<string, string>(); // id -> rationale
  const owners = new Map<string, string>();
  for (const rec of pending) {
    const route = decisionRoute(rec);
    if (!route) continue;
    if (route.op === "resolve") resolved.set(route.ref, route.rationale);
    else owners.set(route.ref, route.owner);
  }
  if (resolved.size === 0 && owners.size === 0) return serverItems;

  return serverItems.map((d) => {
    const key = resolved.has(d.decision_id) ? d.decision_id : resolved.has(d.id) ? d.id : null;
    const ownerKey = owners.has(d.decision_id) ? d.decision_id : owners.has(d.id) ? d.id : null;
    if (key === null && ownerKey === null) return d;
    return {
      ...d,
      ...(ownerKey !== null ? { owner: owners.get(ownerKey) } : {}),
      ...(key !== null
        ? {
            status: "resolved" as const,
            // No local resolved_ts is invented: the server stamps the real one.
            // A rationale the human typed IS shown back, so the detail screen
            // reflects what they wrote rather than blanking it.
            rationale: resolved.get(key) || d.rationale,
          }
        : {}),
    };
  });
}

/**
 * Apply in-flight master-list status changes over a server view.
 *
 * A status change is emitted as a supersede carrier keyed by `supersedes_ref`
 * (the canonical item's capture id) — the same key the server fold collapses
 * on, so a pending carrier and a landed one target the same row.
 */
export function reconcileActions(
  serverItems: MasterItem[],
  records: OutboxRecord[],
  viewFetchedAt: number | null,
): MasterItem[] {
  const pending = pendingRecords(records, viewFetchedAt);
  if (pending.length === 0) return serverItems;

  interface LocalChange {
    ordinal: number;
    status?: StatusValue;
    blocker?: string;
    criticality_tier?: string;
  }
  const changes = new Map<string, LocalChange>();
  for (const rec of pending) {
    const p = rec.envelope.payload;
    const ref = p["supersedes_ref"];
    if (typeof ref !== "string" || ref.length === 0) continue;
    const ordinal = typeof p["change_ordinal"] === "number" ? (p["change_ordinal"] as number) : 0;
    const prev = changes.get(ref);
    // Highest change_ordinal wins — the same INV-4 rule the server fold uses,
    // so two queued edits to one item collapse the way they will server-side.
    if (prev && prev.ordinal > ordinal) continue;
    changes.set(ref, {
      ordinal,
      status: typeof p["status"] === "string" ? (p["status"] as StatusValue) : prev?.status,
      blocker: typeof p["blocker"] === "string" ? (p["blocker"] as string) : prev?.blocker,
      criticality_tier:
        typeof p["criticality_tier"] === "string"
          ? (p["criticality_tier"] as string)
          : prev?.criticality_tier,
    });
  }
  if (changes.size === 0) return serverItems;

  return serverItems.map((item) => {
    const change = changes.get(item.id);
    if (!change) return item;
    return {
      ...item,
      ...(change.status ? { status: change.status } : {}),
      ...(change.blocker ? { blocker: change.blocker } : {}),
      ...(change.criticality_tier ? { criticality_tier: change.criticality_tier } : {}),
    };
  });
}

/**
 * Server-only log entries — the ones this device has no outbox record for.
 *
 * The daily log is not like Decisions or the Action List. Those render FROM the
 * server view with local changes overlaid; the log renders from the device's own
 * outbox, which holds things the server view cannot: the photo blobs the
 * thumbnails come from, and the pre-delivery states (draft/held/sending) the
 * server has never seen. So the server view BACKFILLS here rather than replacing
 * — it supplies exactly the entries this device is missing.
 *
 * That is the whole point of the read leg: after a factory reset, a reinstall,
 * or on a second phone, every entry is server-only and the log would otherwise
 * be empty while the server plainly holds it. It is also how a principal sees a
 * project they did not capture.
 *
 * A carrier record is excluded by id-match like any other: the server already
 * folded corrections into their canonical, and a local carrier's `capture_id` is
 * its own, so matching on the CANONICAL's id (`supersedes_ref` when present) is
 * what keeps a locally-corrected entry from also arriving as a server row.
 */
export function serverOnlyLogEntries<T extends { id: string }>(
  serverEntries: T[],
  records: OutboxRecord[],
): T[] {
  if (serverEntries.length === 0) return [];
  // Only a LOCAL CANONICAL suppresses the server's copy, because only a local
  // canonical is something this device can render in its place.
  //
  // This used to also suppress on a carrier's `supersedes_ref`, reasoning that a
  // correction "stands for" the canonical it supersedes. That holds only when the
  // canonical is ALSO local. #23a made server-only rows editable, so correcting an
  // entry captured on another device left this one holding a carrier and no
  // canonical — the server row was suppressed, nothing replaced it, and the entry
  // VANISHED from the log while sitting safely on the server (reproduced in the
  // browser 2026-07-31: edit an entry from another device, watch it disappear).
  //
  // Dropping the carrier clause loses nothing: the server folds corrections into
  // the canonical before returning it, so the server row already shows the
  // corrected text. A carrier's own capture_id is never a server entry id either,
  // so it can never match — carriers simply have no say here.
  const known = new Set<string>();
  for (const rec of records) {
    const ref = rec.envelope.payload["supersedes_ref"];
    const isCarrier = typeof ref === "string" && ref.length > 0;
    if (!isCarrier) known.add(rec.capture_id);
  }
  return serverEntries.filter((e) => !known.has(e.id));
}

/**
 * Adapt a server-folded log entry into the `OutboxRecord` SHAPE the daily-log
 * row markup reads.
 *
 * The row helpers (`titleOf`/`roomOf`/`criticalityOf`/`timeLabel`/`photoCount`)
 * and `CaptureThumbs` are all written against `OutboxRecord`, and `CaptureThumbs`
 * already self-gates to null when a row has no local blob — the markup
 * anticipated remote rows. Adapting is therefore far less risky than branching
 * the row, and it gives a restored device rows that look like every other row.
 *
 * #23a (2026-07-28) — EDITABLE NOW, and the old read-only note is superseded.
 * This record used to be display-only "by construction": it carried no
 * `tenant_id` and no `project_ref`, so handing it to `submitCorrection` would
 * have thrown at `inheritedProjectRef` rather than emitting an unscoped Event.
 * That was a deferral, not a principle — a restored device could SEE its log and
 * not fix a typo in it, which is the worst half of both worlds.
 *
 * It is safe now because the correction path was already whole: `state:
 * "delivered"` routes `commitEditFromLog` to the supersede branch (never
 * `undoToDraft`, never `deleteLocalRecord` — both are gated on `canUndo`, false
 * here), and `serverOrdinalOf` already threads the SERVER's high-water
 * `change_ordinal` so a restored device's zeroed local cursor cannot allocate
 * under an ordinal already spent and silently lose the INV-4 comparison.
 *
 * The one thing the wire row cannot supply is its own scope, so `scope` is a
 * REQUIRED argument rather than an optional convenience: the caller passes the
 * tenant + project the read itself was made under. `_outbox_kind: "server"` still
 * marks the row for anything that needs to ask (it has no local blobs, so
 * `CaptureThumbs` still self-gates to null).
 *
 * `state: "delivered"` is the truth: the server only returns entries it holds.
 *
 * #23b (2026-07-28): `media` is no longer hardcoded `[]`. The wire row's
 * `source_refs` already carry the capture's content-addressed media keys, so a
 * restored row can at least say "2 photos" instead of silently looking photoless —
 * which read as "the photos are gone" on exactly the device (a reinstall, a second
 * phone) where the human is least sure the data survived. The BYTES still are not
 * fetched here; see `photoRefsFrom` for why.
 */

/** Image extensions among a capture's `source_refs`. Audio and video parts share
 * the same `media/{capture_id}/{sha}.{ext}` grammar, so counting every media ref
 * would report a voice note as a photo. */
const PHOTO_EXT_MIME: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  heic: "image/heic",
};

/**
 * The `primary_photo` media refs a server-folded entry names, as `MediaPartRef`s.
 *
 * DELIBERATELY REFS ONLY, NO BYTES. Rendering an actual thumbnail here would mean
 * fetching the full content-addressed object: `/mark/media` serves the original
 * with no resize variant and an `attachment` disposition, and it needs the bearer,
 * so it cannot even be an `<img src>` — it would have to be fetch + object URL.
 * Measured against live pilot data, one photo is **2.78 MB**; five rows of 56px
 * thumbnails would pull ~13 MB over a jobsite connection to decorate a list. That
 * is not a defensible default, and the fix is a server-side thumbnail variant,
 * which does not exist yet (tracked on the board under #23b).
 *
 * So: the count is honest and free today, and `CaptureThumbs` keeps self-gating to
 * null for these rows because there is no local blob — exactly as it already did.
 */
export function photoRefsFrom(source_refs: string[]): MediaPartRef[] {
  const refs: MediaPartRef[] = [];
  for (const ref of source_refs) {
    const m = /^media\/[^/]+\/([0-9a-f]{64})\.([A-Za-z0-9]+)$/.exec(ref);
    if (!m) continue;
    const mime = PHOTO_EXT_MIME[m[2].toLowerCase()];
    if (!mime) continue; // a .webm voice note is not a photo
    refs.push({
      part_index: refs.length,
      media_ref: ref,
      role: "primary_photo",
      mime,
      sha256: m[1],
      // Unknown on this path: /mark/logs names the ref but not its length (only
      // /mark/photos carries `bytes`). 0 rather than a guess — nothing on the log
      // row displays it, and inventing a number would be worse than admitting it.
      size: 0,
    });
  }
  return refs;
}

/** The tenant + project the log read was made under. #23a: a correction emitted
 * from a restored device has to be scoped, and the wire row does not name its own
 * scope — so the caller supplies the one the read used. Required, not optional:
 * an omitted scope would produce a record that throws at correction time instead
 * of at construction time, which is the wrong end to find out. */
export interface ServerLogScope {
  tenant_id: string;
  project_ref: string;
  skill_version: string;
}

export function serverLogEntryAsRecord(
  entry: {
    id: string;
    text: string;
    ts: string;
    criticality_tier?: string;
    owner?: string;
    room?: string;
    decides?: string;
    /** Attribution, resolved to names by the coordinator. Only ever present on a
     * SERVER row: a locally-captured entry was written by whoever holds this
     * phone, so the byline would be "filed by me" on every row this device made. */
    author?: string;
    author_name?: string;
    edited_by?: string;
    edited_by_name?: string;
    edited_ts?: string;
    /** Content-addressed media keys + the capture id; see `photoRefsFrom`. */
    source_refs?: string[];
    /** Which reports the entry is on. Null/absent = never pinned. */
    report_dest?: "internal_only" | Array<"daily" | "weekly"> | null;
    /** Highest correction ordinal already folded into this server row. */
    change_ordinal?: number;
  },
  scope: ServerLogScope,
): OutboxRecord {
  return {
    capture_id: entry.id,
    client_id: "",
    state: "delivered",
    createdAt: entry.ts,
    retryAt: 0,
    retryCount: 0,
    parts_delivered: {},
    envelope: {
      type: "CaptureLogged",
      schema_version: "1.0",
      capture_id: entry.id,
      client_id: "",
      tenant_id: scope.tenant_id,
      ts: entry.ts,
      skill: "daily_field_log_capture",
      skill_version: scope.skill_version,
      source_refs: entry.source_refs ?? [],
      media: photoRefsFrom(entry.source_refs ?? []),
      parts: [],
      payload: {
        _outbox_kind: "server",
        // #23a: the correction path inherits scope from the canonical
        // (`inheritedProjectRef`, AC-1g-07) rather than re-selecting it, so the
        // record must carry the project the read was made under.
        project_ref: scope.project_ref,
        anchor_text: entry.text,
        transcript: entry.text,
        ...(entry.room ? { room: entry.room } : {}),
        ...(entry.decides ? { decides: entry.decides } : {}),
        ...(entry.criticality_tier ? { criticality_tier: entry.criticality_tier } : {}),
        ...(entry.owner ? { owner: entry.owner } : {}),
        // Carried so the edit screen's destination boxes can show where this
        // entry ACTUALLY goes. `== null` on purpose: absent AND explicit null
        // both mean "never pinned", while `[]` is a real value ("no reports")
        // that must survive — the difference is what makes removal expressible.
        ...(entry.report_dest == null ? {} : { report_dest: entry.report_dest }),
        ...(typeof entry.change_ordinal === "number" ? { change_ordinal: entry.change_ordinal } : {}),
        // Prefer the resolved name, fall back to the id — an entry that says who
        // wrote it is worth more than one that says nothing because no name was set.
        ...(entry.author_name || entry.author
          ? { author_label: entry.author_name || entry.author }
          : {}),
        ...(entry.edited_by_name || entry.edited_by
          ? { edited_by_label: entry.edited_by_name || entry.edited_by }
          : {}),
        ...(entry.edited_ts ? { edited_ts: entry.edited_ts } : {}),
      },
    },
  } as OutboxRecord;
}
